import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, Search, Bell, Sun, Moon, Globe, LogOut, User, ChevronDown, Sprout, X, CheckCheck, PlayCircle } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "../ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";

export default function Topbar({ onMenu }) {
  const { theme, setTheme, language, setLanguage, t, setTourOpen } = useApp();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const launchTour = () => { navigate("/"); setTourOpen(true); };
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);

  const loadNotifs = () => api.get("/notifications").then((r) => setNotifs(r.data.notifications)).catch(() => {});
  useEffect(() => { loadNotifs(); const i = setInterval(loadNotifs, 60000); return () => clearInterval(i); }, []);
  const dismiss = async (id) => { setNotifs((n) => n.filter((x) => x.id !== id)); await api.post("/notifications/dismiss", { id }); };
  const dismissAll = async () => { setNotifs([]); await api.post("/notifications/dismiss-all"); };

  const initials = (user?.name || "F").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <header className="sticky top-0 z-40 h-16 glass border-b flex items-center gap-3 px-4 sm:px-6" data-testid="topbar" style={{ paddingTop: "env(safe-area-inset-top)", height: "calc(4rem + env(safe-area-inset-top))" }}>
      <button onClick={onMenu} className="lg:hidden p-2 rounded-xl hover:bg-accent" data-testid="menu-toggle">
        <Menu className="h-5 w-5" />
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 px-2 sm:px-3 py-2 rounded-xl border bg-card hover:bg-accent text-sm font-medium" data-testid="farm-switcher">
            <Sprout className="h-4 w-4 text-primary shrink-0" />
            <span className="truncate max-w-[84px] sm:max-w-[140px]">{user?.name ? `${user.name.split(" ")[0]}'s Farm` : "My Farm"}</span>
            <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuLabel>Your Farms</DropdownMenuLabel>
          <DropdownMenuItem data-testid="farm-option">{user?.name ? `${user.name.split(" ")[0]}'s Farm` : "My Farm"}</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="hidden md:flex items-center gap-2 flex-1 max-w-md px-3 py-2 rounded-xl border bg-card">
        <Search className="h-4 w-4 text-muted-foreground" />
        <input className="bg-transparent outline-none text-sm flex-1" placeholder={t("search")} data-testid="global-search" />
      </div>

      <div className="flex items-center gap-1.5 ml-auto">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-2 rounded-xl hover:bg-accent" data-testid="lang-toggle">
              <Globe className="h-[18px] w-[18px]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {["English", "हिंदी", "Hinglish"].map((l) => (
              <DropdownMenuItem key={l} onClick={() => setLanguage(l)} data-testid={`lang-${l}`} className={language === l ? "bg-accent" : ""}>
                {l}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <button onClick={() => setTheme(theme === "dark" ? "light" : "dark")} className="p-2 rounded-xl hover:bg-accent" data-testid="theme-toggle">
          {theme === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
        </button>

        <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
          <DropdownMenuTrigger asChild>
            <button className="p-2 rounded-xl hover:bg-accent relative" data-testid="notifications-bell">
              <Bell className="h-[18px] w-[18px]" />
              {notifs.length > 0 && <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-destructive text-[9px] text-white flex items-center justify-center font-bold">{notifs.length}</span>}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <div className="flex items-center justify-between px-2 py-1.5">
              <DropdownMenuLabel className="p-0">Notifications</DropdownMenuLabel>
              {notifs.length > 0 && <button onClick={dismissAll} className="text-xs text-primary flex items-center gap-1" data-testid="dismiss-all-notif"><CheckCheck className="h-3 w-3" />Clear all</button>}
            </div>
            <DropdownMenuSeparator />
            <div className="max-h-80 overflow-y-auto">
              {notifs.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-muted-foreground">You're all caught up 🎉</div>
              ) : notifs.map((n) => (
                <div key={n.id} className="flex items-start gap-2 px-3 py-2.5 hover:bg-accent/50" data-testid={`notif-${n.id}`}>
                  <span className={`mt-1 h-2 w-2 rounded-full shrink-0 ${n.level === "destructive" ? "bg-destructive" : n.level === "warning" ? "bg-warning" : "bg-success"}`} />
                  <div className="flex-1 min-w-0"><p className="text-sm font-medium leading-snug">{n.title}</p><p className="text-xs text-muted-foreground">{n.body}</p></div>
                  <button onClick={() => dismiss(n.id)} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="ml-1" data-testid="avatar-menu">
              <Avatar className="h-9 w-9 border">
                {user?.photoUrl ? <AvatarImage src={user.photoUrl} /> : null}
                <AvatarFallback className="bg-primary text-primary-foreground text-sm">{initials}</AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="flex-col items-start">
              <span>{user?.name}</span>
              <span className="text-xs text-muted-foreground font-normal">{user?.farmRole || "Owner"}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile"><User className="h-4 w-4 mr-2" />{t("profile")}</DropdownMenuItem>
            <DropdownMenuItem onClick={launchTour} data-testid="menu-tour"><PlayCircle className="h-4 w-4 mr-2" />{{ English: "Take the tour", हिंदी: "टूर देखें", Hinglish: "Tour dekhein" }[language] || "Take the tour"}</DropdownMenuItem>
            <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-destructive"><LogOut className="h-4 w-4 mr-2" />{t("logout")}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
